import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { REGISTER_URL } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1531329818183-bba7e80bfecd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwYWlycGxhbmUlMjBzdW5zZXQlMjBjaW5lbWF0aWN8ZW58MHx8fHwxNzgzNzgyOTQ0fDA&ixlib=rb-4.1.0&q=70&w=1920&auto=format"; // Unsplash resizes to 1920px wide and serves AVIF/WebP (2.5 MB → ~50 KB)

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = ({ heroFilter }) => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="grain relative flex min-h-screen items-end overflow-hidden"
    >
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Airliner wing over a cinematic golden-hour sky"
          className="h-full w-full object-cover transition-[filter] duration-[600ms] ease-out"
          style={{ filter: heroFilter }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--aiv-bg-solid)] via-[var(--aiv-bg-solid)]/80 to-[var(--aiv-bg-solid)]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--aiv-bg-solid)]/90 via-transparent to-transparent" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-32 lg:px-10 lg:pb-32">
        <motion.div
          initial="hidden"
          animate="show"
          className="max-w-5xl"
        >
          <motion.div variants={fade} custom={0} className="mb-6 flex items-center gap-3">
            <span className="text-overline rounded-full border border-[var(--aiv-accent)]/40 px-4 py-1.5 text-[var(--aiv-accent)]">
              Oldest Indian Virtual Airline
            </span>
          </motion.div>

          <motion.h1
            variants={fade}
            custom={1}
            className="font-display text-[1.75rem] font-black leading-[1.05] tracking-tight text-[var(--aiv-text)] sm:text-4xl md:whitespace-nowrap md:text-5xl xl:text-6xl"
          >
            Welcome to <span className="text-[var(--aiv-accent)]">Air India</span> Virtual
          </motion.h1>

          <motion.p
            variants={fade}
            custom={2}
            className="mt-6 max-w-xl text-base leading-relaxed text-[var(--aiv-text-2)] lg:text-lg"
          >
            A community of flight-simulator enthusiasts flying MSFS, P3D, FSX and
            X-Plane. Logging virtual skies since 2009 — proudly dedicated to
            Mr. J.R.D Tata, the father of Indian aviation.
          </motion.p>

          <motion.div
            variants={fade}
            custom={3}
            className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center"
          >
            <Link
              to={REGISTER_URL}
              data-testid="hero-register-btn"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#8A1538] px-8 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#A81A45]"
            >
              Become a Pilot
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
            <a
              href="#stats"
              data-testid="hero-stats-btn"
              className="inline-flex items-center justify-center rounded-full border border-white/15 px-8 py-4 text-sm font-semibold text-[var(--aiv-text)] transition-colors duration-200 hover:border-white/40"
            >
              Explore the Fleet Stats
            </a>
          </motion.div>

          <motion.div
            variants={fade}
            custom={4}
            className="mt-10 inline-flex items-center gap-2 text-xs text-[var(--aiv-text-2)]"
          >
            <ShieldCheck className="h-4 w-4 text-[var(--aiv-accent)]" />
            We are NOT affiliated in any manner with the real airline.
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
